// AddEditBusDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Autocomplete from "@mui/material/Autocomplete";

function toHHmm(val) {
  if (!val) return "";
  return String(val).slice(0, 5);
}

function normStops(stops) {
  const list = Array.isArray(stops) ? stops : [];
  return list
    .slice()
    .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
    .map((s, idx) => ({
      stopOrder: s.stopOrder ?? idx + 1,
      location: s.location ?? "",
      campusId: s.campusId ?? "",
      plannedTime: normaliseTime(s.plannedTime),
      stopType: s.stopType ?? "PICKUP",
    }));
}

function toCampusOption(c) {
  const id = c?.id;
  const label = c?.campusName || c?.name || `Campus ${id}`;
  return { kind: "CAMPUS", key: `CAMPUS:${id}`, label, location: label, campusId: id };
}

function toAirportOption(a) {
  const code = (a?.code || "").trim();
  const name = (a?.name || "").trim();
  const label = name && code ? `${name} (${code})` : (name || code || "");
  return { kind: "AIRPORT", key: `AIRPORT:${code}`, label, location: name || code, airportCode: code };
}

function normaliseTime(val) {
  if (!val) return "";
  const s = String(val);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return "";
}

// Order-sensitive signature of a route, used to detect whether the stops of a
// confirmed bus have actually changed (which must force an unconfirm).
function stopsSignature(stops) {
  return JSON.stringify(
    (Array.isArray(stops) ? stops : [])
      .slice()
      .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
      .map((s, i) => ({
        order: i + 1,
        location: String(s.location || "").trim(),
        campusId: String(s.campusId ?? ""),
        plannedTime: normaliseTime(s.plannedTime) || "",
        stopType: String(s.stopType || "").toUpperCase(),
      }))
  );
}

const STOP_TYPES = ["PICKUP", "DROPOFF"];
const STATUS_OPTIONS = ["Planned", "Booked", "Complete"];

// When adding a bus from a campus-scoped view, seed the current campus as a
// pickup stop. Without a stop at that campus the new bus is filtered out of the
// campus view and effectively vanishes, so this keeps it visible by default.
function buildDefaultStops(campuses, defaultCampusId) {
  if (!defaultCampusId || defaultCampusId === "UNASSIGNED") return [];

  const campus = (campuses || []).find(
    (c) => String(c.id) === String(defaultCampusId)
  );
  const location = campus?.campusName || campus?.name || "";
  if (!location) return [];

  return [
    {
      stopOrder: 1,
      location,
      campusId: String(campus.id),
      plannedTime: "",
      stopType: "PICKUP",
    },
  ];
}

export default function AddEditBusDialog({
  open,
  onClose,
  journey, // existing bus DTO for edit; null for add
  campDate, // "YYYY-MM-DD" for add mode
  campuses = [],
  servedAirports = [],
  defaultCampusId, // when adding, seed this campus as a pickup stop
  onSave, // async ({ mode, id, payload }) => Promise
}) {
  const isEdit = Boolean(journey?.id);
  const isConfirmed =
    String(journey?.status || "").trim().toLowerCase() === "confirmed" ||
    Boolean(journey?.confirmedAt);
  const [saving, setSaving] = useState(false);
  const JOURNEY_TYPE_OPTIONS = ["ARRIVALS", "DEPARTURES", "ACTIVITY", "WEEKEND"];

  const [form, setForm] = useState({
    groupName: "",
    capacity: 9,
    campus: "",
    status: "Planned",

    journeyType: "ACTIVITY",
    provider: "",
    contactName: "",
    contactNumber: "",

    stops: [],
    confirmNow: false,
    unconfirmNow: false,
  });

  const stopOptions = useMemo(() => {
    const airportOpts = (servedAirports || []).map(toAirportOption).filter((o) => o.label);
    const campusOpts = (campuses || []).map(toCampusOption).filter((o) => o.label);
    return [
      ...airportOpts.map((o) => ({ ...o, group: "Airports" })),
      ...campusOpts.map((o) => ({ ...o, group: "Campuses" })),
    ];
  }, [servedAirports, campuses]);

  const [stopPickerValue, setStopPickerValue] = useState(null);
  const [stopPickerInput, setStopPickerInput] = useState("");

  useEffect(() => {
    if (!open) return;

    setForm({
      groupName: journey?.groupName ?? "",
      capacity: journey?.capacity ?? 9,
      campus: journey?.campus ?? "",
      status: journey?.status ?? "Planned",

      journeyType: String(journey?.journeyType || "").trim().toUpperCase(),
      direction: journey?.direction ?? "",
      fromLocation: journey?.fromLocation ?? "",
      toLocation: journey?.toLocation ?? "",
      departTime: toHHmm(journey?.departTime),
      arrivalTime: toHHmm(journey?.arrivalTime),
      provider: journey?.provider ?? "",
      contactName: journey?.contactName ?? "",
      contactNumber: journey?.contactNumber ?? "",

      stops: journey?.id
        ? normStops(journey?.stops)
        : buildDefaultStops(campuses, defaultCampusId),
      confirmNow: false,
      unconfirmNow: false,
    });

    setStopPickerValue(null);
    setStopPickerInput("");
  }, [open, journey, campuses, defaultCampusId]);

  const canConfirm = useMemo(
    () => String(form.contactName || "").trim() !== "" && String(form.contactNumber || "").trim() !== "",
    [form.contactName, form.contactNumber]
  );

  // Has a confirmed bus's capacity or route actually changed from what was
  // confirmed? If so the bus MUST be unconfirmed on save — this is enforced from
  // the data, not the checkbox, so the user can't untick their way around it.
  const protectedChanged = useMemo(() => {
    if (!isEdit || !isConfirmed) return false;
    const capacityChanged = Number(form.capacity) !== Number(journey?.capacity);
    const routeChanged = stopsSignature(form.stops) !== stopsSignature(journey?.stops);
    return capacityChanged || routeChanged;
  }, [isEdit, isConfirmed, form.capacity, form.stops, journey?.capacity, journey?.stops]);

  // Unconfirm happens if the user asks for it, or if a protected change forces
  // it. While confirmed, capacity/route are locked until one of those is true —
  // light fields (name, provider, contact) stay editable while confirmed.
  const unconfirmEffective = Boolean(form.unconfirmNow) || protectedChanged;
  const routeLocked = isEdit && isConfirmed && !unconfirmEffective;

  const setField = (key) => (e) => {
    let value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    if (key === "journeyType") value = String(value || "").trim().toUpperCase();
    setForm((f) => ({ ...f, [key]: value }));
  };

  const addStopFromValue = (value) => {
    if (!value) return;

    // free text
    if (typeof value === "string") {
      const label = value.trim();
      if (!label) return;
      setForm((f) => {
        const nextOrder = f.stops.length + 1;
        return {
          ...f,
          stops: [
            ...f.stops,
            {
              stopOrder: nextOrder,
              location: label,
              campusId: "",
              plannedTime: "",
              stopType: nextOrder === 1 ? "PICKUP" : "DROPOFF",
            },
          ],
        };
      });
      return;
    }

    // campus/airport option
    const location = value.location || value.label;
    const campusId = value.kind === "CAMPUS" ? String(value.campusId) : "";

    setForm((f) => {
      const nextOrder = f.stops.length + 1;
      return {
        ...f,
        stops: [
          ...f.stops,
          {
            stopOrder: nextOrder,
            location,
            campusId, // keep as string for your existing payload builder
            plannedTime: "",
            stopType: nextOrder === 1 ? "PICKUP" : "DROPOFF",
          },
        ],
      };
    });
  };

  const updateStop = (idx, key, value) => {
    setForm((f) => {
      const next = f.stops.slice();
      next[idx] = { ...next[idx], [key]: value };
      next.forEach((s, i) => (s.stopOrder = i + 1));
      return { ...f, stops: next };
    });
  };

  const removeStop = (idx) => {
    setForm((f) => {
      const next = f.stops.slice();
      next.splice(idx, 1);
      next.forEach((s, i) => (s.stopOrder = i + 1));
      return { ...f, stops: next };
    });
  };

  // ✅ NEW: move stop up/down
  const moveStop = (fromIdx, toIdx) => {
    setForm((f) => {
      const next = f.stops.slice();
      if (toIdx < 0 || toIdx >= next.length) return f;

      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);

      next.forEach((s, i) => (s.stopOrder = i + 1));
      return { ...f, stops: next };
    });
  };

  const validate = () => {
    if (!String(form.groupName || "").trim()) return "Bus name is required.";

    const cap = Number(form.capacity);
    if (!Number.isFinite(cap) || cap <= 0)
      return "Capacity must be a positive number.";

    if (!campDate && !isEdit)
      return "No date selected.";

    const badLocation = (form.stops || []).find(
      (s) => String(s.location || "").trim() === ""
    );
    if (badLocation)
      return "Each stop must have a location (or remove the blank stop).";

    const badTime = (form.stops || []).find((s) => {
      const raw = String(s.plannedTime || "").trim();
      return raw !== "" && normaliseTime(raw) === "";
    });

    if (badTime)
      return "Stop time must be HH:mm (e.g. 09:30) or left blank.";

    // Effective stop type mirrors the per-row Select default (first stop
    // PICKUP, the rest DROPOFF) so the check matches what the user sees.
    const effectiveStopType = (s, idx) =>
      String(s.stopType || (idx === 0 ? "PICKUP" : "DROPOFF")).toUpperCase();
    const stops = form.stops || [];
    const hasPickup = stops.some((s, i) => effectiveStopType(s, i) === "PICKUP");
    const hasDropoff = stops.some((s, i) => effectiveStopType(s, i) === "DROPOFF");
    if (!hasPickup || !hasDropoff)
      return "Add at least one PICKUP and one DROPOFF stop.";

    // A stop counts as one of our campuses only if it carries a campusId
    // (set when picked from the Campuses list, not free-typed). That campusId
    // is what attributes the journey to a campus, so require at least one.
    const hasCampusStop = stops.some((s) => String(s.campusId ?? "").trim() !== "");
    if (!hasCampusStop)
      return "Add at least one stop at one of your campuses (choose it from the Campuses list).";

    if (form.confirmNow && !canConfirm)
      return "Contact name and number are required to confirm.";

    if (!JOURNEY_TYPE_OPTIONS.includes(String(form.journeyType || "").trim().toUpperCase())) {
      return "Journey type must be ARRIVALS, DEPARTURES, ACTIVITY, or WEEKEND.";
    }

    return null;
  };

  const buildStopsPayload = () =>
    (form.stops || []).map((s, idx) => ({
      stopOrder: idx + 1,
      location: String(s.location || "").trim() || null,
      campusId: s.campusId === "" ? null : Number(s.campusId),
      plannedTime: normaliseTime(s.plannedTime) || null,   // ✅ important
      stopType: s.stopType || null,
    }));

  const buildMetaPayload = () => ({
    journeyType: String(form.journeyType || "").trim().toUpperCase() || null,
    provider: form.provider || null,
    contactName: form.contactName || null,
    contactNumber: form.contactNumber || null,
    clearFields: []
  });

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSaving(true);
    try {
      if (!isEdit) {
        await onSave?.({
          mode: "add",
          payload: {
            campDate,
            groupName: String(form.groupName).trim(),
            capacity: Number(form.capacity),
            campus: form.campus ?? "",
            status: form.status ?? "Planned",
            meta: buildMetaPayload(),
            stops: buildStopsPayload(),
          },
        });
      } else {
        // Never send "Confirmed" via the update endpoint — the backend rejects
        // it (confirmation is owned by /confirm). Sending null leaves the
        // current status untouched, so a confirmed bus stays confirmed while
        // light fields are edited; unconfirming is handled by saveTransportGroup.
        const statusToSend =
          String(form.status || "").trim().toLowerCase() === "confirmed"
            ? null
            : (form.status ?? "Planned");

        await onSave?.({
          mode: "edit",
          id: journey.id,
          payload: {
            groupName: String(form.groupName).trim(),
            capacity: Number(form.capacity),
            campus: form.campus ?? "",
            status: statusToSend,
            meta: buildMetaPayload(),
            stops: buildStopsPayload(),
            confirm: Boolean(form.confirmNow),
            unconfirm: unconfirmEffective,
          },
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Edit bus" : "Add bus"}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Base */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField fullWidth label="Bus name" value={form.groupName} onChange={setField("groupName")} />
            <TextField
              label="Capacity"
              type="number"
              value={form.capacity}
              onChange={setField("capacity")}
              disabled={routeLocked}
              sx={{ width: { xs: "100%", sm: 160 } }}
              helperText={routeLocked ? "Unconfirm to change capacity" : ""}
            />
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={setField("status")}
              disabled={routeLocked}
              sx={{ width: { xs: "100%", sm: 180 } }}
              helperText={isEdit ? "Use Confirm below to set Confirmed" : ""}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider />

          {/* Meta */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              fullWidth
              label="Journey type"
              value={form.journeyType}
              onChange={setField("journeyType")}
            >
              {JOURNEY_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            
          </Stack>

          

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            
            <TextField fullWidth label="Provider" value={form.provider} onChange={setField("provider")} />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField fullWidth label="Contact name" value={form.contactName} onChange={setField("contactName")} />
            <TextField fullWidth label="Contact number" value={form.contactNumber} onChange={setField("contactNumber")} />
          </Stack>

          {/* Confirm / Unconfirm */}
          {isEdit && !isConfirmed && (
            <FormControlLabel
              control={<Checkbox checked={form.confirmNow} onChange={setField("confirmNow")} disabled={!canConfirm} />}
              label={
                <Stack spacing={0}>
                  <Typography variant="body2">Confirm this bus</Typography>
                  {!canConfirm && (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Add contact name and number to enable confirmation
                    </Typography>
                  )}
                </Stack>
              }
            />
          )}

          {isEdit && isConfirmed && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={unconfirmEffective}
                  disabled={protectedChanged}
                  onChange={setField("unconfirmNow")}
                />
              }
              label={
                <Stack spacing={0}>
                  <Typography variant="body2">Unconfirm this bus</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {protectedChanged
                      ? "Capacity/route changed — saving will unconfirm this bus. Re-confirm afterwards."
                      : "Reverts this bus to unconfirmed so it can be edited or removed"}
                  </Typography>
                </Stack>
              }
            />
          )}

          <Divider />

          {/* Stops */}
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography fontWeight={800}>Stops</Typography>
              {routeLocked && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Unconfirm to change the route
                </Typography>
              )}
            </Stack>
            <Autocomplete
              options={stopOptions}
              groupBy={(o) => o.group}
              getOptionLabel={(o) => (typeof o === "string" ? o : (o.label || ""))}
              freeSolo
              disabled={routeLocked}
              value={stopPickerValue}
              onChange={(_, value) => {
                addStopFromValue(value);
                setStopPickerValue(null);
                setStopPickerInput("");
              }}
              inputValue={stopPickerInput}
              onInputChange={(_, newInputValue, reason) => {
                // Ignore the "reset" that fires on selection — onChange already
                // clears the field. Track typing and the clear (X) button.
                if (reason !== "reset") setStopPickerInput(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add stop (campus, airport, or type an address)"
                  placeholder="e.g. Bathgate, Edinburgh Airport, Premier Inn Haymarket…"
                />
              )}
            />

              {(form.stops || []).length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No stops yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {form.stops.map((s, idx) => (
                    <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                      <TextField label="#" value={idx + 1} disabled sx={{ width: 60 }} />
                      <TextField
                        fullWidth
                        label="Location"
                        value={s.location}
                        onChange={(e) => updateStop(idx, "location", e.target.value)}
                        disabled={routeLocked}
                      />

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton
                          size="small"
                          aria-label="Move stop up"
                          disabled={routeLocked || idx === 0}
                          onClick={() => moveStop(idx, idx - 1)}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Move stop down"
                          disabled={routeLocked || idx === form.stops.length - 1}
                          onClick={() => moveStop(idx, idx + 1)}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <TextField
                        select
                        label="Type"
                        value={s.stopType}
                        onChange={(e) => updateStop(idx, "stopType", e.target.value)}
                        disabled={routeLocked}
                        sx={{ width: 140 }}
                      >
                        {STOP_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        label="Time (HH:mm)"
                        value={s.plannedTime || ""}
                        onChange={(e) => updateStop(idx, "plannedTime", e.target.value)}
                        onBlur={() => updateStop(idx, "plannedTime", normaliseTime(s.plannedTime))}
                        disabled={routeLocked}
                        sx={{ width: 160 }}
                        placeholder="09:30"
                      />

                      <IconButton
                        aria-label="Remove stop"
                        onClick={() => removeStop(idx)}
                        disabled={routeLocked}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );  
}